import { Button, IconButton, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { When } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon, VailAccordion, VailTextField } from '@/components'
import { BasicCustomComponentSettings } from '@/screens/ComponentLibrary/components'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { ComponentType } from '@/utils/api'

export type UserGroupingSettings = {
  groupingQuestionText: string
  buttons: Array<{ question: string; name: string }>
}
export type UserGroupingForm = {
  settings: UserGroupingSettings
  name: string
  description: string
  type: ComponentType
}

export const SettingsTab = () => {
  const { componentID, orgID } = useParams()
  const current = useGetCustomComponent<UserGroupingSettings>(componentID)
  const create = useCreateCustomComponent<UserGroupingSettings>(orgID)
  const update = useUpdateCustomComponent<UserGroupingSettings>(componentID)
  const form = useForm<UserGroupingForm>({
    defaultValues: {
      settings: {
        buttons: [
          {
            name: '',
            question: '',
          },
        ],
        groupingQuestionText: '',
      },
    },
  })
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'settings.buttons',
    rules: {
      validate: {
        atLeastTwo: (value) => value.length >= 2 || 'At least two are required',
      },
    },
  })
  const navigate = useNavigate()
  const { reset } = form
  const onSubmit = form.handleSubmit(async (data) => {
    if (componentID) return update.mutateAsync({ ...data, id: componentID })
    const res = await create.mutateAsync(data)
    return navigate(`../${res?.id}`)
  })

  useEffect(() => {
    if (!current.data) return
    const { settings, name, description, type } = current.data
    reset({ description, name, settings, type })
  }, [reset, current.data])

  return (
    <FormProvider {...form}>
      <Stack component="form" rowGap={2} onSubmit={onSubmit}>
        <Button
          id={safeCypressID('Save User Grouping')}
          sx={{
            alignSelf: 'flex-end',
            mb: 2,
            mt: 'auto',
          }}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
        <BasicCustomComponentSettings
          componentType={ComponentType.userGrouping}
        />
        <VailAccordion defaultExpanded title="Configuration">
          <VailTextField
            multiline
            control={form.control}
            label="Grouping Question Text"
            minRows={6}
            name="settings.groupingQuestionText"
          />
          {fieldArray.fields.map((field, index) => (
            <Stack
              key={field.id}
              alignItems="center"
              direction="row"
              justifyContent="center"
              spacing={2}
            >
              <VailTextField
                control={form.control}
                label="Grouping Question Button"
                name={`settings.buttons.${index}.question`}
                rules={{
                  minLength: {
                    message: 'Minimum length is 3',
                    value: 3,
                  },
                  required: 'Required',
                }}
              />

              <VailTextField
                control={form.control}
                label="Grouping Name"
                name={`settings.buttons.${index}.name`}
              />
              <IconButton
                size="small"
                sx={{ height: 'fit-content' }}
                onClick={() => fieldArray.remove(index)}
              >
                <Icon name="delete" />
              </IconButton>
            </Stack>
          ))}
          <When condition={!!form.formState.errors.settings?.buttons}>
            <Typography color="error" component="pre" variant="caption">
              Form must have at least two buttons
            </Typography>
          </When>
          <Button
            id={safeCypressID('Add Button')}
            startIcon={<Icon name="add" />}
            sx={{ alignSelf: 'flex-start' }}
            variant="outlined"
            onClick={() =>
              fieldArray.append({
                name: '',
                question: '',
              })
            }
          >
            Add Button
          </Button>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
